import React, { useState, useEffect, useCallback } from 'react';
import { useAuthFetch } from '../hooks/useAuthFetch';

interface PlatformStats {
  docId: string;
  counts: Record<string, number>;
  tokens: Record<string, number>;
  durations: Record<string, number>;
  [key: string]: any;
}

interface PipelineRun {
  traceId: string;
  traceType: string;
  status: string;
  totalDurationMs: number;
  totalTokens: number;
  model?: string;
  errorCategory?: string;
  resumeId?: string;
  orgId?: string;
  startedAt?: string;
  spans?: Record<string, { durationMs: number; [key: string]: any }>;
}

interface OrgSummary {
  orgId: string;
  orgName?: string;
  totalEnrichments: number;
  totalRapidMatches: number;
  totalMatches: number;
  totalTokens: number;
  totalErrors: number;
  cacheHits: number;
  cacheMisses: number;
  days: number;
}

type Tab = 'overview' | 'runs' | 'orgs';

export const AdminPage: React.FC = () => {
  const authFetch = useAuthFetch();
  const [tab, setTab] = useState<Tab>('overview');
  const [stats, setStats] = useState<PlatformStats[]>([]);
  const [runs, setRuns] = useState<PipelineRun[]>([]);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/admin/stats?days=${days}`);
      if (res.ok) {
        const data = await res.json();
        setStats(data.stats || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [authFetch, days]);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/pipeline-runs?limit=100');
      if (res.ok) {
        const data = await res.json();
        setRuns(data.runs || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [authFetch]);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/admin/orgs?days=${days}`);
      if (res.ok) {
        const data = await res.json();
        setOrgs(data.orgs || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [authFetch, days]);

  useEffect(() => {
    if (tab === 'overview') fetchStats();
    else if (tab === 'runs') fetchRuns();
    else if (tab === 'orgs') fetchOrgs();
  }, [tab, fetchStats, fetchRuns, fetchOrgs]);

  // Aggregate stats for overview
  const totals = stats.reduce(
    (acc, s) => {
      const c = s.counts || {};
      const t = s.tokens || {};
      acc.enrichments += c.enrichment || 0;
      acc.rapidMatches += c.rapid_match || 0;
      acc.matches += c.match || 0;
      acc.errors += c.errors || 0;
      acc.cacheHits += c.cacheHits || 0;
      acc.cacheMisses += c.cacheMisses || 0;
      acc.inputTokens += t.input || 0;
      acc.outputTokens += t.output || 0;
      return acc;
    },
    { enrichments: 0, rapidMatches: 0, matches: 0, errors: 0, cacheHits: 0, cacheMisses: 0, inputTokens: 0, outputTokens: 0 }
  );

  const cacheHitRate = totals.cacheHits + totals.cacheMisses > 0
    ? Math.round((totals.cacheHits / (totals.cacheHits + totals.cacheMisses)) * 100)
    : 0;

  const tabStyle = (t: Tab): React.CSSProperties => ({
    padding: '8px 16px',
    border: 'none',
    background: tab === t ? 'var(--accent-primary, #4285f4)' : 'transparent',
    color: tab === t ? '#fff' : 'var(--text-secondary)',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: tab === t ? 600 : 400,
    fontSize: 'var(--font-size-sm, 14px)',
  });

  const cardStyle: React.CSSProperties = {
    background: 'var(--bg-card, #fff)',
    border: '1px solid var(--border-primary, #e0e0e0)',
    borderRadius: 8,
    padding: 16,
    textAlign: 'center',
  };

  const statLabel: React.CSSProperties = {
    fontSize: 'var(--font-size-xs, 12px)',
    color: 'var(--text-muted, #888)',
    marginBottom: 4,
  };

  const statValue: React.CSSProperties = {
    fontSize: 24,
    fontWeight: 700,
  };

  return (
    <div className="v2-page" style={{ maxWidth: 1100 }}>
      <div className="v2-page-header">
        <h1>Platform Admin</h1>
        <p style={{ color: 'var(--text-muted)' }}>Observability dashboard — pipeline runs, token usage, and org stats</p>
      </div>

      {/* Quick links to other admin tools */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <a
          href="#/admin/deal-desk"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 8,
            border: '1px solid var(--border-primary, #e0e0e0)',
            background: 'var(--bg-card, #fff)',
            color: 'var(--text-primary)',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: 14,
          }}
        >
          Deal Desk →
        </a>
        <a
          href="#/admin/pricing"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 8,
            border: '1px solid var(--border-primary, #e0e0e0)',
            background: 'var(--bg-card, #fff)',
            color: 'var(--text-primary)',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: 14,
          }}
        >
          Pricing &amp; Limits Editor →
        </a>
        <a
          href="#/admin/migration-center"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 8,
            border: '1px solid var(--border-primary, #e0e0e0)',
            background: 'var(--bg-card, #fff)',
            color: 'var(--text-primary)',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: 14,
          }}
        >
          Migration Center →
        </a>
      </div>

      {/* Tabs + days selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button style={tabStyle('overview')} onClick={() => setTab('overview')}>Overview</button>
        <button style={tabStyle('runs')} onClick={() => setTab('runs')}>Pipeline Runs</button>
        <button style={tabStyle('orgs')} onClick={() => setTab('orgs')}>Orgs</button>
        <div style={{ marginLeft: 'auto' }}>
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-primary, #e0e0e0)', background: 'var(--bg-card, #fff)', color: 'var(--text-primary)' }}
          >
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Loading...</p>}

      {/* Overview */}
      {tab === 'overview' && !loading && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
            <div style={cardStyle}><div style={statLabel}>Enrichments</div><div style={statValue}>{totals.enrichments}</div></div>
            <div style={cardStyle}><div style={statLabel}>Rapid Matches</div><div style={statValue}>{totals.rapidMatches}</div></div>
            <div style={cardStyle}><div style={statLabel}>Matches</div><div style={statValue}>{totals.matches}</div></div>
            <div style={cardStyle}><div style={statLabel}>Errors</div><div style={{...statValue, color: totals.errors > 0 ? '#ea4335' : undefined}}>{totals.errors}</div></div>
            <div style={cardStyle}><div style={statLabel}>Cache Hit Rate</div><div style={statValue}>{cacheHitRate}%</div></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div style={cardStyle}><div style={statLabel}>Input Tokens</div><div style={statValue}>{(totals.inputTokens / 1000).toFixed(1)}k</div></div>
            <div style={cardStyle}><div style={statLabel}>Output Tokens</div><div style={statValue}>{(totals.outputTokens / 1000).toFixed(1)}k</div></div>
            <div style={cardStyle}><div style={statLabel}>Total Tokens</div><div style={statValue}>{((totals.inputTokens + totals.outputTokens) / 1000).toFixed(1)}k</div></div>
            <div style={cardStyle}><div style={statLabel}>Cache Hits</div><div style={statValue}>{totals.cacheHits}</div></div>
            <div style={cardStyle}><div style={statLabel}>Cache Misses</div><div style={statValue}>{totals.cacheMisses}</div></div>
          </div>
        </>
      )}

      {/* Pipeline Runs */}
      {tab === 'runs' && !loading && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm, 13px)' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-primary, #e0e0e0)', textAlign: 'left' }}>
                <th style={{ padding: '8px 6px', width: 20 }}></th>
                <th style={{ padding: '8px 6px' }}>Type</th>
                <th style={{ padding: '8px 6px' }}>Status</th>
                <th style={{ padding: '8px 6px' }}>Duration</th>
                <th style={{ padding: '8px 6px' }}>Tokens</th>
                <th style={{ padding: '8px 6px' }}>Model</th>
                <th style={{ padding: '8px 6px' }}>Org</th>
                <th style={{ padding: '8px 6px' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>No pipeline runs yet</td></tr>
              )}
              {runs.map(r => {
                const isExpanded = expandedRun === r.traceId;
                const spanEntries = r.spans ? Object.entries(r.spans) : [];
                const hasSpans = spanEntries.length > 0;
                return (
                  <React.Fragment key={r.traceId}>
                    <tr
                      style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--border-secondary, #f0f0f0)', cursor: hasSpans ? 'pointer' : 'default' }}
                      onClick={() => hasSpans && setExpandedRun(isExpanded ? null : r.traceId)}
                    >
                      <td style={{ padding: '6px', fontSize: 10, color: 'var(--text-muted)', userSelect: 'none' }}>
                        {hasSpans ? (isExpanded ? '▼' : '▶') : ''}
                      </td>
                      <td style={{ padding: '6px' }}><span style={{ background: 'var(--bg-muted, #f5f5f5)', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>{r.traceType}</span></td>
                      <td style={{ padding: '6px' }}>
                        <span style={{
                          color: r.status === 'indexed' || r.status === 'success' || r.status === 'cache_hit' ? '#34a853' : r.status === 'failed' ? '#ea4335' : '#fbbc04',
                          fontWeight: 600,
                        }}>{r.status}</span>
                        {r.errorCategory && <span style={{ color: '#ea4335', fontSize: 11, marginLeft: 4 }}>({r.errorCategory})</span>}
                      </td>
                      <td style={{ padding: '6px', fontVariantNumeric: 'tabular-nums' }}>{r.totalDurationMs ? `${(r.totalDurationMs / 1000).toFixed(1)}s` : '-'}</td>
                      <td style={{ padding: '6px', fontVariantNumeric: 'tabular-nums' }}>{r.totalTokens ? r.totalTokens.toLocaleString() : '-'}</td>
                      <td style={{ padding: '6px', fontSize: 11, color: 'var(--text-muted)' }}>{r.model || '-'}</td>
                      <td style={{ padding: '6px', fontSize: 11, fontFamily: 'monospace' }}>{r.orgId ? r.orgId.slice(0, 8) : '-'}</td>
                      <td style={{ padding: '6px', fontSize: 11, color: 'var(--text-muted)' }}>
                        {r.startedAt ? new Date(r.startedAt).toLocaleString() : '-'}
                      </td>
                    </tr>
                    {isExpanded && hasSpans && (
                      <tr>
                        <td colSpan={8} style={{ padding: '4px 6px 12px 30px', background: 'var(--bg-muted, #fafafa)', borderBottom: '1px solid var(--border-secondary, #f0f0f0)' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
                            Span Waterfall &mdash; {spanEntries.length} step{spanEntries.length !== 1 ? 's' : ''}
                          </div>
                          {spanEntries.map(([name, span]) => {
                            const pct = r.totalDurationMs > 0 ? Math.max(2, (span.durationMs / r.totalDurationMs) * 100) : 50;
                            const tokens = (span.inputTokens || 0) + (span.outputTokens || 0);
                            return (
                              <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                                <div style={{ width: 120, fontSize: 11, color: 'var(--text-secondary)', textAlign: 'right', flexShrink: 0, fontFamily: 'monospace' }}>{name}</div>
                                <div style={{ flex: 1, background: 'var(--border-secondary, #e8e8e8)', borderRadius: 3, height: 18, position: 'relative', overflow: 'hidden' }}>
                                  <div style={{
                                    width: `${pct}%`,
                                    height: '100%',
                                    borderRadius: 3,
                                    background: span.durationMs > 10000 ? '#ea4335' : span.durationMs > 3000 ? '#fbbc04' : '#4285f4',
                                  }} />
                                </div>
                                <div style={{ width: 55, fontSize: 11, fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)', flexShrink: 0 }}>
                                  {span.durationMs >= 1000 ? `${(span.durationMs / 1000).toFixed(1)}s` : `${span.durationMs}ms`}
                                </div>
                                <div style={{ width: 70, fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>
                                  {tokens > 0 ? `${tokens.toLocaleString()} tok` : ''}
                                </div>
                              </div>
                            );
                          })}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Orgs */}
      {tab === 'orgs' && !loading && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm, 13px)' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-primary, #e0e0e0)', textAlign: 'left' }}>
                <th style={{ padding: '8px 6px' }}>Workspace</th>
                <th style={{ padding: '8px 6px' }}>Enrichments</th>
                <th style={{ padding: '8px 6px' }}>Rapid Matches</th>
                <th style={{ padding: '8px 6px' }}>Matches</th>
                <th style={{ padding: '8px 6px' }}>Tokens</th>
                <th style={{ padding: '8px 6px' }}>Errors</th>
                <th style={{ padding: '8px 6px' }}>Cache H/M</th>
              </tr>
            </thead>
            <tbody>
              {orgs.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>No org data yet</td></tr>
              )}
              {orgs.map(o => (
                <tr key={o.orgId} style={{ borderBottom: '1px solid var(--border-secondary, #f0f0f0)' }}>
                  <td style={{ padding: '6px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{o.orgName || 'Unknown Workspace'}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>{o.orgId.slice(0, 12)}</div>
                  </td>
                  <td style={{ padding: '6px', fontVariantNumeric: 'tabular-nums' }}>{o.totalEnrichments}</td>
                  <td style={{ padding: '6px', fontVariantNumeric: 'tabular-nums' }}>{o.totalRapidMatches}</td>
                  <td style={{ padding: '6px', fontVariantNumeric: 'tabular-nums' }}>{o.totalMatches}</td>
                  <td style={{ padding: '6px', fontVariantNumeric: 'tabular-nums' }}>{o.totalTokens.toLocaleString()}</td>
                  <td style={{ padding: '6px', fontVariantNumeric: 'tabular-nums', color: o.totalErrors > 0 ? '#ea4335' : undefined }}>{o.totalErrors}</td>
                  <td style={{ padding: '6px', fontVariantNumeric: 'tabular-nums' }}>{o.cacheHits}/{o.cacheMisses}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
